import { BrandColorExamples } from "@/pages/Design/BrandColorExamples";
import AppLayout from "@/components/layouts/AppLayout";
import type { ReactNode } from "react";
import { Box, Button, Card, Heading, SimpleGrid, Stack, Text, VStack } from "@chakra-ui/react";

type Swatch = {
  name: string;
  role: "Primary" | "Accent" | "Alternative";
  base: string;
  hover: string;
  active: string; // using semantic "bold" token
  subtle: string; // using semantic "subtle" token
};

const SWATCHES: Swatch[] = [
  {
    name: "Primary (brand)",
    role: "Primary",
    base: "primary-base",
    hover: "primary-hover",
    active: "primary-bold",
    subtle: "primary-subtle",
  },
];

const Block = ({ label, color }: { label: string; color: string }) => (
  <VStack gap={2} align="center">
    <Box w="36" h="20" borderRadius="md" border="1px solid" borderColor="gray.200" bg={color} />
    <Text fontSize="xs" color="gray.600" fontFamily="mono">
      {color}
    </Text>
    <Text fontSize="xs" color="gray.500">
      {label}
    </Text>
  </VStack>
);

const OnColorBlock = ({ label, background, onColor }: { label: string; background: string; onColor: string }) => (
  <VStack gap={2} align="center">
    <Box
      w="36"
      h="20"
      borderRadius="md"
      border="1px solid"
      borderColor="gray.200"
      bg={background}
      color={onColor}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Text fontSize="sm">Aa</Text>
    </Box>
    <Stack direction="row" align="center" gap={2}>
      <Text fontSize="10px" color="gray.500">
        Swatch
      </Text>
      <Box w="5" h="5" borderRadius="sm" border="1px solid" borderColor="gray.300" bg={onColor} />
    </Stack>
    <Text fontSize="xs" color="gray.600" fontFamily="mono">
      {onColor}
    </Text>
    <Text fontSize="xs" color="gray.500">
      {label}
    </Text>
  </VStack>
);

const Palette = ({ swatch }: { swatch: Swatch }) => (
  <Card.Root>
    <Card.Body>
      <Stack direction="row" justify="space-between" align="center" mb={3}>
        <Box>
          <Text fontSize="sm" fontWeight="medium" color="gray.800">
            {swatch.name}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {swatch.role}
          </Text>
        </Box>
        <Stack direction="row" gap={2}>
          <Button size="sm" bg={swatch.base} color="on-primary-base" _hover={{ bg: swatch.hover }}>
            Button
          </Button>
          <Button size="sm" bg={swatch.hover} color="on-primary-base">
            Hover
          </Button>
          <Button size="sm" bg={swatch.active} color="on-primary-bold">
            Active
          </Button>
        </Stack>
      </Stack>

      <SimpleGrid columns={{ base: 2, sm: 4 }} gap={6}>
        <Block label="Base" color={swatch.base} />
        <Block label="Hover" color={swatch.hover} />
        <Block label="Active" color={swatch.active} />
        <Block label="Subtle" color={swatch.subtle} />
      </SimpleGrid>

      <Box mt={4} p={3} borderRadius="md" border="1px solid" borderColor="gray.200" bg={swatch.subtle}>
        <Text fontSize="sm" color="gray.700">
          Example area on subtle background
        </Text>
      </Box>

      <Box mt={6}>
        <Text mb={2} fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase" letterSpacing="wide">
          On-colors
        </Text>
        <SimpleGrid columns={3} gap={6}>
          <OnColorBlock label="Primary on Subtle" background={swatch.subtle} onColor="on-primary-subtle" />
          <OnColorBlock label="Primary on Base" background={swatch.base} onColor="on-primary-base" />
          <OnColorBlock label="Primary on Bold" background={swatch.active} onColor="on-primary-bold" />
        </SimpleGrid>
      </Box>
    </Card.Body>
  </Card.Root>
);

const Colors = () => {
  return (
    <VStack gap={6} align="stretch" maxW="5xl">
      <Box>
        <Heading size="lg" mb={2}>
          Design Colors
        </Heading>
        <Text color="gray.600">Preview palettes to help choose a primary.</Text>
      </Box>

      <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
        {SWATCHES.map((s) => (
          <Palette key={s.name} swatch={s} />
        ))}
      </SimpleGrid>

      {/* Add the comprehensive examples */}
      <Box>
        <Heading size="lg" mb={4}>
          Comprehensive Color Examples
        </Heading>
        <BrandColorExamples />
      </Box>
    </VStack>
  );
};

Colors.layout = (page: ReactNode) => <AppLayout>{page}</AppLayout>;

export default Colors;
